import React, { useEffect, useMemo } from 'react';
import { DatePicker, Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { datePickerFormat } from '../../../../i18n';
import type { Planting } from '../../../../models/Planting';
import type { PlantingDispositionKind, PlantingDispositionPayload } from '../../../../api/plantingsApi';
import { useSaveOptional } from '../../../../contexts/WorldContext';
import { disableBeforeOpenDay } from '../../../../utils/openDayDate';

const { Text } = Typography;

type FormValues = {
  kind: PlantingDispositionKind;
  quantityAffected?: number;
  effectiveDate: dayjs.Dayjs;
  reason: string;
};

interface Props {
  open: boolean;
  planting: Planting;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (payload: PlantingDispositionPayload) => Promise<void>;
  /** Save cursor D (last computed day); falls back to the save context cursor. */
  cursorDate?: string | null;
}

const OPTION_KINDS: PlantingDispositionKind[] = ['partial_writeoff', 'written_off', 'partial_sale', 'sold'];

const PlantingDispositionModal: React.FC<Props> = ({
  open,
  planting,
  loading,
  onCancel,
  onSubmit,
  cursorDate: cursorDateProp,
}) => {
  const { t } = useTranslation('plantings');
  const [form] = Form.useForm<FormValues>();
  const save = useSaveOptional();
  // Выбытие датируется днём курсора D или позже: «прожила день D, со D+1 её нет».
  const rawCursor = cursorDateProp || save?.cursor?.simDate || save?.cursor?.sim_date || null;
  const cursorDate = rawCursor ? rawCursor.slice(0, 10) : null;
  const options = useMemo(
    () => OPTION_KINDS.map((value) => ({ value, label: t(`disposition.kind.${value}`) })),
    [t],
  );
  const quantity = Math.max(1, Number(planting.quantity || 1));
  const isBatch = planting.plantingType === 'batch' || quantity > 1;
  const kind = Form.useWatch('kind', form);
  const isFullKind = kind === 'written_off' || kind === 'sold';

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      kind: isBatch ? 'partial_writeoff' : 'written_off',
      quantityAffected: quantity,
      effectiveDate: cursorDate ? dayjs(cursorDate) : dayjs(),
      reason: '',
    });
  }, [cursorDate, form, isBatch, open, quantity]);

  useEffect(() => {
    if (!open || !isFullKind) return;
    form.setFieldValue('quantityAffected', quantity);
  }, [form, isFullKind, open, quantity]);

  const quantityRules = useMemo(() => {
    if (!isBatch) return [];
    return [
      { required: true, message: t('disposition.quantityRequired') },
      { type: 'number' as const, min: 1, max: quantity, message: t('disposition.quantityRange', { max: quantity }) },
    ];
  }, [isBatch, quantity, t]);

  const handleOk = async () => {
    const values = await form.validateFields();
    await onSubmit({
      kind: values.kind,
      quantityAffected: isBatch ? values.quantityAffected : 1,
      effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
      reason: values.reason.trim(),
    });
  };

  return (
    <Modal
      title={t('disposition.title')}
      open={open}
      okText={t('common:actions.apply')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="kind" label={t('disposition.operation')} rules={[{ required: true, message: t('disposition.operationRequired') }]}>
          <Select options={options} />
        </Form.Item>

        {isBatch && (
          <Form.Item name="quantityAffected" label={t('disposition.quantity')} rules={quantityRules}>
            <InputNumber min={1} max={quantity} precision={0} disabled={isFullKind} addonAfter={t('units.pcs')} style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Text type="secondary">{t('disposition.currentStock', { count: quantity })}</Text>

        <Form.Item
          name="effectiveDate"
          label={t('common:labels.date')}
          rules={[{ required: true, message: t('disposition.dateRequired') }]}
          style={{ marginTop: 16 }}
        >
          <DatePicker format={datePickerFormat()} style={{ width: '100%' }} disabledDate={disableBeforeOpenDay(cursorDate)} />
        </Form.Item>

        <Form.Item
          name="reason"
          label={t('disposition.reason')}
          rules={[
            { required: true, message: t('disposition.reasonRequired') },
            { transform: (value) => String(value || '').trim(), min: 1, message: t('disposition.reasonRequired') },
          ]}
        >
          <Input.TextArea rows={3} placeholder={t('disposition.reasonPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PlantingDispositionModal;
